import { z } from "zod";

/**
 * Importation et lecture des CV et pièces (devis, modules « Import et lecture
 * des CV » et « Reprise du papier »). Tout se passe sur le serveur :
 * texte (Poppler / Tesseract) → obfuscation (moteur Anonymum) → extraction des
 * champs par un modèle de langage LOCAL (Ollama) sur le texte obfusqué →
 * proposition à valider par une personne avant tout enregistrement.
 */
export const JOB_STATUSES = ["en_attente", "en_cours", "a_valider", "applique", "rejete", "echec"] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  en_attente: "En attente",
  en_cours: "Lecture en cours",
  a_valider: "À valider",
  applique: "Appliqué",
  rejete: "Rejeté",
  echec: "Échec",
};

export const JOB_STEPS = ["texte", "obfuscation", "extraction", "proposition"] as const;
export type JobStep = (typeof JOB_STEPS)[number];

/** Mêmes valeurs que EDUCATION_LEVELS de x-candidats (un module n'importe pas un autre), du plus bas au plus haut. */
export const CV_EDUCATION_LEVELS = ["aucun", "primaire", "secondaire", "professionnel", "collegial", "universitaire_1", "universitaire_2", "universitaire_3", "autre"] as const;
export type CvEducationLevel = (typeof CV_EDUCATION_LEVELS)[number];

export const CV_EDUCATION_LABELS: Record<CvEducationLevel, string> = {
  aucun: "Aucun diplôme",
  primaire: "Primaire",
  secondaire: "Secondaire",
  professionnel: "Formation professionnelle",
  collegial: "Collégial / technique",
  universitaire_1: "Universitaire 1er cycle",
  universitaire_2: "Universitaire 2e cycle",
  universitaire_3: "Universitaire 3e cycle",
  autre: "Autre",
};

/** Niveau de langue déclaré dans un CV, ramené à une échelle simple. */
export const LANGUAGE_LEVELS = ["natif", "courant", "avance", "intermediaire", "notions"] as const;
export type LanguageLevel = (typeof LANGUAGE_LEVELS)[number];
export const LANGUAGE_LEVEL_LABELS: Record<LanguageLevel, string> = {
  natif: "Langue maternelle",
  courant: "Courant",
  avance: "Avancé",
  intermediaire: "Intermédiaire",
  notions: "Notions",
};

/** Date de CV normalisée par le serveur : « 2019 » (année seule) ou « 2019-03 ». */
const cvDate = z.string().regex(/^\d{4}(-\d{2})?$/);

export const cvExperienceSchema = z.object({
  title: z.string().nullable(),
  employer: z.string().nullable(),
  location: z.string().nullable(),
  start: cvDate.nullable(),
  end: cvDate.nullable(),
  /** Poste occupé à la date du CV (« aujourd'hui », « en cours »…). */
  current: z.boolean(),
  /** Durée calculée par le serveur à partir des dates, jamais par le modèle. */
  months: z.number().int().min(0).nullable(),
  highlights: z.array(z.string()),
});
export type CvExperience = z.infer<typeof cvExperienceSchema>;

export const cvEducationSchema = z.object({
  degree: z.string().nullable(),
  field: z.string().nullable(),
  school: z.string().nullable(),
  location: z.string().nullable(),
  year: z.number().int().min(1950).max(2100).nullable(),
  level: z.enum(CV_EDUCATION_LEVELS).nullable(),
});
export type CvEducation = z.infer<typeof cvEducationSchema>;

/**
 * Profil structuré d'un CV : la matière du rapport d'analyse et, plus tard, de
 * l'assistant de recherche. Le modèle local remplit les listes ; le serveur
 * normalise les dates et calcule les totaux (`totals`).
 */
export const cvProfileSchema = z.object({
  /** Métier principal ou poste visé. */
  title: z.string().nullable(),
  /** Une phrase d'accroche factuelle, pas un paragraphe. */
  summary: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  nationality: z.string().nullable(),
  experiences: z.array(cvExperienceSchema),
  education: z.array(cvEducationSchema),
  skills: z.object({
    technical: z.array(z.string()),
    tools: z.array(z.string()),
    soft: z.array(z.string()),
  }),
  languages: z.array(z.object({ language: z.string(), level: z.enum(LANGUAGE_LEVELS).nullable() })),
  certifications: z.array(z.object({ name: z.string(), issuer: z.string().nullable(), year: z.number().int().nullable() })),
  /** Permis de conduire, cartes de compétence, habilitations. */
  licenses: z.array(z.string()),
  /** Trois à cinq points forts, en quelques mots chacun. */
  strengths: z.array(z.string()),
  totals: z.object({
    /** Expérience cumulée sans double compte des périodes qui se chevauchent. */
    experienceMonths: z.number().int().min(0).nullable(),
    lastTitle: z.string().nullable(),
    highestLevel: z.enum(CV_EDUCATION_LEVELS).nullable(),
  }),
});
export type CvProfile = z.infer<typeof cvProfileSchema>;

/** Comment le texte a été obtenu, et ce qu'il a coûté (affiché dans le rapport, journalisable). */
export interface CvReadingMeta {
  method: "pdf-texte" | "pdf-ocr" | "image-ocr" | "docx" | "texte";
  pages: number | null;
  textLength: number;
  model: string | null;
  durationMs: number;
  warnings: string[];
}

/** Champs que la lecture d'un CV peut proposer pour une fiche candidat. */
export const proposalFieldsSchema = z.object({
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  sex: z.enum(["F", "M", "X"]).nullable().optional(),
  birthDate: z.string().nullable().optional(),
  nationality: z.string().nullable().optional(),
  countryResidence: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  profession: z.string().nullable().optional(),
  educationLevel: z.enum(CV_EDUCATION_LEVELS).nullable().optional(),
  educationDetail: z.string().nullable().optional(),
  experienceYears: z.number().int().min(0).max(60).nullable().optional(),
  languages: z.array(z.string()).optional(),
  passportNumber: z.string().nullable().optional(),
  passportExpiresAt: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
});
export type ProposalFields = z.infer<typeof proposalFieldsSchema>;

/** Ce que l'utilisateur valide : les champs retenus (sous-ensemble corrigé de la proposition). */
export const applyProposalSchema = z.object({
  fields: proposalFieldsSchema,
  /** Dossier cible ; absent = créer un nouveau dossier à partir de ces champs. */
  candidateId: z.string().min(1).optional().nullable(),
});
export type ApplyProposalInput = z.infer<typeof applyProposalSchema>;

export interface ExtractionJob {
  id: string;
  documentId: string;
  candidateId: string | null;
  lotId: string | null;
  kind: "cv" | "piece";
  status: JobStatus;
  step: JobStep | null;
  error: string | null;
  textLength: number | null;
  model: string | null;
  /** Source de chaque champ proposé : « identite » (détection déterministe) ou « modele ». */
  proposal: { fields: ProposalFields; sources: Record<string, "identite" | "modele">; warnings: string[] } | null;
  /** Profil structuré (jetons restaurés), avec la proposition. */
  profile: CvProfile | null;
  method: CvReadingMeta["method"] | null;
  durationMs: number | null;
  originalName: string | null;
  createdBy: string | null;
  createdAt: number | string;
  startedAt: number | string | null;
  finishedAt: number | string | null;
  reviewedBy: string | null;
  reviewedAt: number | string | null;
}

/**
 * Lot d'analyse : des CV déposés ensemble (ex. « Soudeurs — novembre »), lus
 * par la même chaîne et rassemblés dans un rapport comparatif. Le nom est
 * libre : ne pas y mettre de donnée de candidat.
 */
export const lotInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
});
export type LotInput = z.infer<typeof lotInputSchema>;
export const lotUpdateSchema = lotInputSchema.partial().extend({ archived: z.boolean().optional() });
export const lotDocumentsSchema = z.object({ documentIds: z.array(z.string().min(1)).min(1).max(200) });

export interface ExtractionLot {
  id: string;
  name: string;
  description: string | null;
  createdBy: string | null;
  createdAt: number | string;
  archivedAt: number | string | null;
  /** Nombre de lectures par statut, et au total. */
  counts: Partial<Record<JobStatus, number>>;
  total: number;
}

/** Une lecture de CV telle que la présentent le rapport d'un lot et le vivier. */
export interface CvAnalysis {
  jobId: string;
  documentId: string;
  lotId: string | null;
  candidateId: string | null;
  status: JobStatus;
  error: string | null;
  originalName: string | null;
  identity: { firstName: string | null; lastName: string | null; email: string | null; phone: string | null };
  profile: CvProfile | null;
  method: CvReadingMeta["method"] | null;
  durationMs: number | null;
  model: string | null;
  warnings: string[];
  createdAt: number | string;
  finishedAt: number | string | null;
}

export interface LotReport {
  lot: ExtractionLot;
  items: CvAnalysis[];
}

/** Filtres du vivier (toutes les lectures de CV abouties, tous lots confondus). */
export const analysisQuerySchema = z.object({
  /** Mots cherchés dans le poste, les compétences, les formations, l'identité… (tous requis). */
  q: z.string().trim().max(200).optional(),
  lotId: z.string().min(1).optional(),
  niveau: z.enum(CV_EDUCATION_LEVELS).optional(),
  /** Expérience cumulée minimale, en années. */
  experienceMin: z.coerce.number().int().min(0).max(60).optional(),
  langue: z.string().trim().max(40).optional(),
  tri: z.enum(["recent", "experience", "nom"]).default("recent"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type AnalysisQuery = z.infer<typeof analysisQuerySchema>;

export interface AnalysisPage {
  items: CvAnalysis[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Assistant de recherche (Claude Haiku, Anthropic) : il ne reçoit que les
 * profils ANONYMISÉS du vivier, désignés par une référence opaque (P1, P2…),
 * sans nom, coordonnées, employeurs, établissements, ville ni nationalité. Les
 * références sont remplacées par les vrais noms sur le serveur, au retour.
 */
export const assistantQuestionSchema = z.object({
  question: z.string().trim().min(3).max(2000),
  /** Limiter la recherche à un lot ; absent = tout le vivier. */
  lotId: z.string().min(1).nullable().optional(),
  /** Échanges précédents de la conversation, dans leur version anonymisée (références P1…). */
  history: z
    .array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().max(8000) }))
    .max(20)
    .default([]),
});
export type AssistantQuestion = z.infer<typeof assistantQuestionSchema>;

export interface AssistantCandidate {
  /** Rang dans la réponse (1 = le plus apte). */
  rank: number;
  /** Adéquation estimée par l'assistant, de 0 à 100. */
  score: number;
  reasons: string[];
  gaps: string[];
  analysis: CvAnalysis;
}

export interface AssistantAnswer {
  /** Réponse, références remplacées par les noms (pour l'écran). */
  answer: string;
  /** La même, avec les références (à renvoyer dans `history` au tour suivant). */
  anonymizedAnswer: string;
  /** La question telle qu'elle a été envoyée (noms remplacés par les références). */
  anonymizedQuestion: string;
  candidates: AssistantCandidate[];
  /** Nombre de profils transmis à l'assistant pour cette question. */
  consulted: number;
  model: string;
}

export interface AssistantState {
  available: boolean;
  model: string;
}
